// The Qualtrics "Finish" creative resolves its display language in this order:
//   1. the global JS variable `window.qualtricsLanguage` (set in src/index.js)
//   2. the browser's `navigator.languages`
//   3. the language the creative was originally authored in
// Step 2 does not reliably honor the browser's preference order, so setting the variable
// preempts it rather than relying on it.
export const DEFAULT_QUALTRICS_LANGUAGE = 'EN'

// NOTE: FamilySearch uses BCP47 locale codes; Qualtrics uses its own language codes, documented at
// https://www.qualtrics.com/support/survey-platform/survey-module/survey-tools/translate-survey/#AvailableLanguageCodes
// Only locales this app ships (see src/locales/) are mapped. Any other locale -- including
// Qualtrics languages we don't support, like Hebrew or Welsh -- resolves to DEFAULT_QUALTRICS_LANGUAGE.
export const localeToQualtricsLanguageMap = {
  ar: 'AR', // Arabic
  bg: 'BG', // Bulgarian
  ceb: 'CEB', // Cebuano
  cs: 'CS', // Czech
  da: 'DA', // Danish
  de: 'DE', // German
  el: 'EL', // Greek
  en: 'EN', // English
  eo: 'EO', // Esperanto (pseudo-localization debug locale)
  es: 'ES', // Spanish
  et: 'ET', // Estonian
  fa: 'FA', // Persian
  fi: 'FI', // Finnish
  fj: DEFAULT_QUALTRICS_LANGUAGE, // Fijian -- no Qualtrics code
  fr: 'FR', // French
  gn: DEFAULT_QUALTRICS_LANGUAGE, // Guarani -- no Qualtrics code
  hi: 'HI', // Hindi
  hr: 'HR', // Croatian
  ht: DEFAULT_QUALTRICS_LANGUAGE, // Haitian Creole -- no Qualtrics code
  hu: 'HU', // Hungarian
  hy: 'HYE', // Armenian
  id: 'ID', // Indonesian
  is: 'ISL', // Icelandic
  it: 'IT', // Italian
  ja: 'JA', // Japanese
  ka: 'KAT', // Georgian
  km: 'KM', // Khmer
  ko: 'KO', // Korean
  lt: 'LT', // Lithuanian
  lv: 'LV', // Latvian
  mg: DEFAULT_QUALTRICS_LANGUAGE, // Malagasy -- no Qualtrics code
  mk: 'MK', // Macedonian
  mn: 'MN', // Mongolian
  ms: 'MS', // Malay
  nl: 'NL', // Dutch
  no: 'NO', // Norwegian
  pl: 'PL', // Polish
  pt: 'PT', // Portuguese
  ro: 'RO', // Romanian
  ru: 'RU', // Russian
  sk: 'SK', // Slovak
  sl: 'SL', // Slovenian
  sm: DEFAULT_QUALTRICS_LANGUAGE, // Samoan -- no Qualtrics code
  sq: 'SQI', // Albanian
  sr: 'SR', // Serbian
  sv: 'SV', // Swedish
  sw: 'SW', // Swahili
  ta: 'TA', // Tamil
  te: 'TEL', // Telugu
  th: 'TH', // Thai
  tl: 'TGL', // Tagalog
  to: DEFAULT_QUALTRICS_LANGUAGE, // Tongan -- no Qualtrics code
  tr: 'TR', // Turkish
  uk: 'UK', // Ukrainian
  ur: 'UR', // Urdu
  vi: 'VI', // Vietnamese
  zh: 'ZH-T', // FamilySearch 'zh' is Traditional Chinese
  'zh-hans': 'ZH-S', // Simplified Chinese
}

/**
 * @param {string} [locale] an i18n.language app locale, e.g. 'en', 'zh-hans'
 * @returns {string} an uppercase Qualtrics language code, defaulting to English
 */
export function getQualtricsLanguage(locale) {
  return localeToQualtricsLanguageMap[String(locale ?? '').toLowerCase()] ?? DEFAULT_QUALTRICS_LANGUAGE
}

/**
 * Sets window.qualtricsLanguage (the global JS variable the Qualtrics "Finish" creative reads),
 * and keeps it in sync as the given i18next instance's language changes.
 * @param {{ language: string, on: (event: 'languageChanged', callback: () => void) => void }} i18nInstance
 */
export function initQualtricsLanguage(i18nInstance) {
  const syncQualtricsLanguage = () => {
    window.qualtricsLanguage = getQualtricsLanguage(i18nInstance.language)
  }
  syncQualtricsLanguage()
  i18nInstance.on('languageChanged', syncQualtricsLanguage)
}
